// Plays public/sound/notification.mp3 when a new in-app notification arrives.
// The bell's poll is not a user gesture, so browsers (and the Android WebView)
// block playback until the page has been interacted with — we "unlock" the one
// shared <audio> element on the first tap/keypress.

const SOUND_URL = '/sound/notification.mp3';
const SOUND_VOLUME = 0.7;

export const SOUND_PREF_KEY = 'pms.notificationSound';
const LAST_PLAYED_KEY = 'pms.notificationSound.lastPlayedAt';

let audio: HTMLAudioElement | null = null;

function getAudio(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio(SOUND_URL);
    audio.preload = 'auto';
    audio.volume = SOUND_VOLUME;
  }
  return audio;
}

function isAutoplayBlocked(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'NotAllowedError';
}

export function playNotificationSound(): void {
  const el = getAudio();
  el.currentTime = 0;
  el.play().catch((err: unknown) => {
    // Autoplay blocked (no user gesture yet) is expected — the badge still updates.
    if (!isAutoplayBlocked(err)) throw err;
  });
}

// A muted play+pause inside a real gesture marks the element as user-activated,
// so later plays from the poll are allowed.
function unlock(): void {
  const el = getAudio();
  el.muted = true;
  el.play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
    })
    .catch((err: unknown) => {
      if (!isAutoplayBlocked(err)) throw err;
    })
    .finally(() => {
      el.muted = false;
    });
}

let unlockRegistered = false;

export function registerNotificationSoundUnlock(): void {
  if (unlockRegistered) return;
  unlockRegistered = true;
  const handler = () => {
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
    unlock();
  };
  window.addEventListener('pointerdown', handler);
  window.addEventListener('keydown', handler);
}

export function isNotificationSoundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_PREF_KEY) !== 'off';
  } catch {
    return true; // storage unavailable (private mode) — keep the default: on
  }
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_PREF_KEY, enabled ? 'on' : 'off');
  } catch {
    /* storage unavailable — the choice lasts for this page only */
  }
}

// How long the winning tab keeps the per-notification lock, so a tab polling at
// the same moment is refused. Longer than one poll interval.
const CHIME_LOCK_HOLD_MS = 60_000;

// Values are fixed-width UTC ISO strings, so plain string comparison is ordering.
function alreadyPlayed(latestAt: string): boolean {
  try {
    const last = localStorage.getItem(LAST_PLAYED_KEY);
    return last !== null && last >= latestAt;
  } catch {
    return false; // storage unavailable — let this tab chime
  }
}

function markPlayed(latestAt: string): void {
  try {
    localStorage.setItem(LAST_PLAYED_KEY, latestAt);
  } catch {
    /* storage unavailable — the Web Lock alone still dedupes concurrent tabs */
  }
}

// Several open tabs must chime once, not once each. localStorage alone races
// when tabs poll at the same instant (e.g. all refetch on reconnect), so the
// claim is made under a Web Lock (atomic across tabs). The lock is missing on
// plain-http origins; there we fall back to localStorage only.
export function claimNotificationChime(latestAt: string): Promise<boolean> {
  if (alreadyPlayed(latestAt)) return Promise.resolve(false);
  if (!('locks' in navigator)) {
    markPlayed(latestAt);
    return Promise.resolve(true);
  }
  return new Promise<boolean>((resolve) => {
    void navigator.locks.request(`pms.notificationSound:${latestAt}`, { ifAvailable: true }, (lock) => {
      if (!lock || alreadyPlayed(latestAt)) {
        resolve(false);
        return undefined;
      }
      markPlayed(latestAt);
      resolve(true);
      return new Promise<void>((release) => setTimeout(release, CHIME_LOCK_HOLD_MS));
    });
  });
}
